import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import {
  activityLogService,
  type ActivityLog,
  type ActivityType,
} from '../../services/activityLogService';
import { Pagination } from '../../components/common/Pagination';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { usePaginationState } from '../../hooks/usePaginationState';
import { useLanguage } from '../../features/language/useLanguage';
import JsonDiff from './components/JsonDiff';

const MODULES = [
  'inventory',
  'supplier_master',
  'lot',
  'sample',
  'purchase_order',
  'work_order',
  'cupping_session',
  'sales_order',
  'customer_master',
];

const TYPE_BADGE: Record<ActivityType, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
};

const shortId = (id: string) => (id && id.length > 8 ? `${id.slice(0, 8)}…` : id || '—');

export default function ActivityLogsList() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });

  const [module, setModule] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  resetOnChange([module, startDate, endDate]);

  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['activityLogs', { module, startDate, endDate, page, size }],
    queryFn: () =>
      activityLogService.list({
        module: module || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        size,
      }),
  });

  const logs = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  const filterSelectClass =
    'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500';
  const filterLabelClass = 'block text-xs font-medium text-gray-500 uppercase mb-1';
  const thClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase';

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/settings')}
        data-test-id="activity-logs-back-button"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary-700 focus:outline-none focus:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('activityLogs.backToSettings', undefined, 'Back to Settings')}
      </button>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('activityLogs.title', undefined, 'Activity Logs')}
          </h1>
          <p className="text-sm text-gray-500">
            {t(
              'activityLogs.description',
              undefined,
              'Audit trail of all create, update, and delete operations across business modules.',
            )}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={filterLabelClass}>
            {t('activityLogs.filters.module', undefined, 'Module')}
          </label>
          <select
            value={module}
            onChange={(e) => setModule(e.target.value)}
            className={filterSelectClass}
          >
            <option value="">{t('activityLogs.filters.allModules', undefined, 'All modules')}</option>
            {MODULES.map((m) => (
              <option key={m} value={m}>
                {m
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={filterLabelClass}>
            {t('activityLogs.filters.startDate', undefined, 'Start date')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={filterSelectClass}
          />
        </div>
        <div>
          <label className={filterLabelClass}>
            {t('activityLogs.filters.endDate', undefined, 'End date')}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={filterSelectClass}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <LoadingSpinner text={t('common.loading', undefined, 'Loading…')} />
        ) : isError ? (
          <div className="p-8 text-center text-red-500">
            {t('activityLogs.loadError', undefined, 'Failed to load activity logs.')}
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('activityLogs.empty', undefined, 'No activity recorded for these filters.')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-6 py-3" />
                  <th className={thClass}>{t('activityLogs.columns.time', undefined, 'Time')}</th>
                  <th className={thClass}>{t('activityLogs.columns.module', undefined, 'Module')}</th>
                  <th className={thClass}>{t('activityLogs.columns.type', undefined, 'Type')}</th>
                  <th className={thClass}>{t('activityLogs.columns.reference', undefined, 'Reference')}</th>
                  <th className={thClass}>{t('activityLogs.columns.user', undefined, 'User')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <Row
                    key={log.id}
                    log={log}
                    expanded={expandedId === log.id}
                    onToggle={() => toggle(log.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function Row({
  log,
  expanded,
  onToggle,
}: {
  log: ActivityLog;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useLanguage();
  const time = new Date(log.created_at).toLocaleString();
  const referenceDisplay = log.reference_label?.trim() || shortId(log.reference_id);
  const userDisplay = log.user_name?.trim() || shortId(log.user_id);
  const formatActivityType = (type: string) => type.replace(/_/g, ' ').toUpperCase();
  return (
    <React.Fragment>
      <tr
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-6 py-4 text-sm text-gray-500">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="p-1 hover:bg-gray-200 rounded-full focus:outline-none"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </td>
        <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">{time}</td>
        <td className="px-6 py-4 text-sm text-gray-600 font-semibold">{formatActivityType(log.module)}</td>
        <td className="px-6 py-4 text-sm">
          <span
            className={`px-2 py-0.5 rounded text-sm font-medium uppercase ${TYPE_BADGE[log.activity_type]}`}
          >
            {t(`activityLogs.types.${log.activity_type}`, undefined, log.activity_type)}
          </span>
        </td>
        <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate max-w-xs">
          {referenceDisplay}
        </td>
        <td className="px-6 py-4 text-sm text-gray-700 truncate max-w-xs">{userDisplay}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            {log.activity_type === 'update' && log.changes ? (
              <JsonDiff
                before={log.changes.before ?? {}}
                after={log.changes.after ?? {}}
              />
            ) : (
              <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-auto max-h-64">
                {JSON.stringify(log.properties ?? {}, null, 2)}
              </pre>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}
